import { FamilyApplicationResp } from '@/apis/family_application/requests.ts'
import { LogUserActivity } from '@/apis/users/requests'
import { buttonVariants } from '@/app/common/components/Button.tsx'
import MultiQueryPageLoader from '@/app/common/components/PageLoader/MultiQueryPageLoader.tsx'
import {
  CONTACT_EMAIL,
  CONTACT_PHONE_NUMBER,
  CONTACT_PHONE_NUMBER_FORMATTED,
  US_STATES_ID,
} from '@/app/common/constants.ts'
import {
  BankIcon,
  ChatIcon,
  MobileIcon,
  StudentIcon,
  UserCheckIcon,
} from '@/app/common/icons/general.tsx'
import {
  capitalizeAndRemoveUnderscores,
  cn,
  currencyFormatter,
} from '@/app/common/utilities.ts'
import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import axiosInstance from '@/libs/axios_config.ts'
import { CommonQueries } from '@/queries/common/queries.ts'
import { UserQueries } from '@/queries/users/queries'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shad/ui/dialog.tsx'
import { selectFamilyApplication } from '@/storage/applications/familyApplicationSlice.ts'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice.ts'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { z } from 'zod'
import StudentStatusBadge from '../family_application/components/StudentStatusBadge.tsx'
import { LOG_TYPES } from '../family_application/constants'
import DefaultAvatar from './components/DefaultAvatar.tsx'
import InfoField from './components/InfoField.tsx'

const scrollMarginClass = 'scroll-mt-24'

const studentPurseSchema = z.object({
  student_id: z.number().int().positive(),
  sequence_number: z.string().nullable(),
  purse_id: z.number().int().positive(),
  balance_available: z.number(),
  awarded_amount: z.number(),
  awarded_year_initial: z.string().nullable(),
})

export default function MyAccountPage() {
  const application = useSelector(selectFamilyApplication)
  const programId = useSelector(selectProgramID)

  // something went wrong here.
  // we probably shouldn't have to pass a student id to get the award details of all students.
  // I don't have time to work on a change right now.
  // So this will have to do.
  // TODO: Update the endpoint so we can get the award details of all students.
  // https://github.com/SoftPoint-LLC/SP_ACE_API/pull/2840/changes
  const firstStudentScholarshipApplicationId =
    application.students[0]?.scholarship_application_id ?? null

  const awardDetailsQuery = useQuery({
    queryKey: [
      'award-details',
      programId,
      firstStudentScholarshipApplicationId,
    ],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `/family_application/${firstStudentScholarshipApplicationId}/award_details`,
        {
          params: {
            program_id: programId,
          },
        }
      )
      return z.array(studentPurseSchema).parse(response.data)
    },
  })

  const userQuery = useQuery(
    UserQueries.getAuthenticatedUser(application.applicant_id)
  )

  const statesQuery = useQuery(CommonQueries.FetchStateByContry(US_STATES_ID))

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'My Account page',
      }) // Log page view
      hasLoggedPageView.current = true // Mark as executed
    }
  }, [])

  function scrollToSection(sectionId: string) {
    const section = document.getElementById(sectionId)
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const queries = [userQuery, statesQuery, awardDetailsQuery] as const

  return (
    <MultiQueryPageLoader queries={queries}>
      {([user, states, awardDetails]) => {
        const studentAwardDetails = awardDetails
        const userState = states.data.find(
          (state) => state.id === user.global_state_id
        )

        function getFullAddress(application: FamilyApplicationResp) {
          // 2025-12-08 - Pontus
          // Since we're getting data from ServiceNow this scenario should not happen.
          // However, we'll add this check to be safe.
          if (!application.address || !userState?.name) {
            return null
          }

          return `${application.address}, ${application.city}, ${userState.name} ${application.zip_code}`
        }

        return (
          <div className="max-w-screen-2xl w-full mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div className="flex items-center justify-between ">
                <div className="text-2xl font-medium ">
                  Applicant Information
                </div>
              </div>
              <Dialog>
                <DialogTrigger
                  className={buttonVariants({
                    variant: 'outline',
                  })}
                >
                  Report Incorrect Information
                </DialogTrigger>
                <DialogContent className="sm:max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Update Your Information</DialogTitle>
                    <DialogDescription>
                      This information is managed by ACE Scholarships. If you
                      need to update any details, please contact us and we'll
                      assist you.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex gap-4 py-4">
                    <div className="flex-1 min-w-0 flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <MobileIcon className="h-5 w-5 text-gray-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-600">
                          Phone
                        </div>
                        <a
                          href={`tel:${CONTACT_PHONE_NUMBER}`}
                          className="text-base text-gray-900 truncate"
                        >
                          {CONTACT_PHONE_NUMBER_FORMATTED}
                        </a>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <ChatIcon className="size-5 text-gray-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-600">
                          Email
                        </div>
                        <a
                          href={`mailto:${CONTACT_EMAIL}`}
                          className="text-base text-gray-900 hover:underline truncate block"
                        >
                          {CONTACT_EMAIL}
                        </a>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose className="w-full" asChild>
                      <button
                        className={buttonVariants({
                          className: 'w-full',
                          variant: 'outline',
                        })}
                      >
                        Close
                      </button>
                    </DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-12 gap-8">
              <aside className="col-span-3">
                <Card className="col-span-3 h-fit sticky top-20">
                  <CardContent>
                    <div className="mb-6">
                      <div className="text-gray-800 font-medium mb-2">
                        Applicant Details
                      </div>
                      <button
                        onClick={() => scrollToSection('applicant-details')}
                        className="flex items-center gap-x-2 py-2 hover:bg-gray-100 rounded-md px-2 w-full text-sm text-gray-800"
                      >
                        <UserCheckIcon />
                        {application.first_name} {application.last_name}
                      </button>
                      <button
                        onClick={() => scrollToSection('income-data')}
                        className="flex items-center gap-x-2 py-2 hover:bg-gray-100 rounded-md px-2 w-full text-sm text-gray-800"
                      >
                        <BankIcon /> Income Data
                      </button>
                    </div>
                    <div>
                      <div className="font-medium text-gray-800 mb-3">
                        Students
                      </div>
                      <div className="space-y-1">
                        {application.students.map((student) => {
                          const studentAwardDetailsList =
                            studentAwardDetails.filter(
                              (award) => award.student_id === student.id
                            ) ?? []

                          if (studentAwardDetailsList.length === 0) return null

                          return (
                            <div key={student.id} className="mb-4">
                              <div className="space-y-2">
                                {studentAwardDetailsList.map((award) => (
                                  <button
                                    key={award.purse_id}
                                    onClick={() =>
                                      scrollToSection(`student-${student.id}`)
                                    }
                                    className="flex gap-2 text-left w-full bg-white hover:bg-gray-50 transition-colors rounded-md py-2"
                                  >
                                    <StudentIcon />
                                    <div>
                                      <div className="text-sm font-medium text-gray-800 mb-1">
                                        {student.first_name} {student.last_name}
                                      </div>
                                      <div className="flex flex-col gap-1">
                                        <span className="text-sm font-normal text-gray-600">
                                          {currencyFormatter.format(
                                            award.awarded_amount
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </aside>
              <main className="col-span-9 flex flex-col items-center max-w-screen-xl mx-auto w-full gap-8">
                <Card className=" w-full">
                  <CardContent className="flex items-center justify-between">
                    <div className="flex items-center gap-x-2">
                      <DefaultAvatar
                        firstName={application.first_name}
                        lastName={application.last_name}
                      />
                      <div>
                        <div className="text-gray-800 text-lg font-medium">
                          {application.first_name} {application.last_name}
                        </div>
                        <div className="text-gray-600 text-sm font-normal">
                          {application.email}
                        </div>
                      </div>
                    </div>
                    <div>
                      <InfoField label="Applicant ID" value={user.id} />
                    </div>
                  </CardContent>
                </Card>
                <Card
                  id="applicant-details"
                  className={cn('w-full', scrollMarginClass)}
                >
                  <CardHeader className="text-gray-800 font-medium text-xl">
                    {application.first_name} {application.last_name} - Applicant
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-8">
                      <InfoField
                        label="Name"
                        value={`${application.first_name} ${application.last_name}`}
                      />
                      <InfoField
                        label="Address"
                        value={getFullAddress(application)}
                      />
                      <InfoField
                        label="Date Of Birth"
                        value={application.date_of_birth}
                      />
                      <InfoField
                        label="Military Exemption Applicable?"
                        value={application.is_military_exemption ? 'Yes' : 'No'}
                      />
                    </div>
                  </CardContent>
                </Card>
                <Card
                  id="income-data"
                  className={cn(scrollMarginClass, 'w-full')}
                >
                  <CardHeader className="text-gray-800 font-medium text-xl">
                    Income Data
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-8">
                      <InfoField
                        label="Household Size"
                        value={application.household_inhabitants}
                      />
                      <InfoField
                        label="Income Verified By"
                        value={application.income_evaluated_by}
                      />
                      <InfoField
                        label="Filing Type"
                        value={application.household_filed_federal_income}
                      />

                      <InfoField
                        label="Gross Income"
                        value={
                          Number.isFinite(
                            application.verified_feed_adjustment_gross_income
                          )
                            ? currencyFormatter.format(
                                application.verified_feed_adjustment_gross_income!
                              )
                            : null
                        }
                      />
                      <InfoField
                        label="Tax Year"
                        value={application.verified_income_tax_year}
                      />
                      <InfoField
                        label="Spouse Income"
                        value={
                          Number.isFinite(
                            application.spouse_verified_gross_income
                          )
                            ? currencyFormatter.format(
                                application.spouse_verified_gross_income!
                              )
                            : null
                        }
                      />
                      <InfoField
                        label="Spouse Income Year"
                        value={application.spouse_verified_tax_year}
                      />
                    </div>
                  </CardContent>
                </Card>
                {application.students.map((student) => {
                  const studentAwardDetailsList =
                    studentAwardDetails.filter(
                      (award) => award.student_id === student.id
                    ) ?? []

                  return (
                    <div
                      key={student.id}
                      id={`student-${student.id}`}
                      className={cn(scrollMarginClass, 'w-full space-y-6')}
                    >
                      <Card className="w-full">
                        <CardHeader className="flex items-center justify-between">
                          <div className="text-gray-800 font-medium text-xl">
                            {student.first_name} {student.last_name} - ID:{' '}
                            {student.id}
                          </div>
                          <StudentStatusBadge
                            status={student.application_status}
                            subStatus={
                              student.scholarship_application_sub_status
                            }
                          />
                        </CardHeader>
                        <CardContent className="space-y-8">
                          <InfoField
                            label="Name"
                            value={`${student.first_name} ${student.last_name}`}
                          />
                          <InfoField
                            label="Date of Birth"
                            value={student.date_of_birth}
                          />
                          <InfoField label="Grade" value={student.grade} />
                          <InfoField
                            label="Relationship"
                            value={
                              student.relationship
                                ? capitalizeAndRemoveUnderscores(
                                    student.relationship
                                  )
                                : null
                            }
                          />
                          {studentAwardDetailsList.map((award) => (
                            <div key={award.purse_id} className="space-y-8">
                              <InfoField
                                label="Award Amount"
                                value={currencyFormatter.format(
                                  award.awarded_amount
                                )}
                              />
                              <InfoField
                                label="Current Balance"
                                value={currencyFormatter.format(
                                  award.balance_available
                                )}
                              />
                              <InfoField
                                label="Year of Initial Award"
                                value={award.awarded_year_initial}
                              />
                              <InfoField
                                label="Sequence Number"
                                value={award.sequence_number}
                              />
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    </div>
                  )
                })}
              </main>
            </div>
          </div>
        )
      }}
    </MultiQueryPageLoader>
  )
}
